"use client"

import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { SidebarHeader } from "./SidebarHeader"
import { NewChatButton } from "./NewChatButton"
import { ChatList } from "./ChatList"
import { CommunicationSection } from "./CommunicationSection"
import { AuthSection } from "./AuthSection"
import { MobileFloatingMenu } from "./MobileFloatingMenu"
import { FeedbackModal } from "@/components/modals/FeedbackModal"
import { DoctorCallModal } from "@/components/modals/DoctorCallModal"
import type { SidebarProps } from "./types"
import { useIsMobile } from "@/hooks/useMediaQuery"
import { chatAPI } from "@/lib/api"

type SidebarState = 'expanded' | 'collapsed' | 'transition-opening' | 'transition-closing'

export function Sidebar({
  chats,
  activeChat,
  onSelectChat,
  onNewChat,
  onDeleteChat,
}: SidebarProps) {
  const isMobile = useIsMobile()
  const [isCollapsed, setIsCollapsed] = useState(false) // Desktop only state
  // Initialize sidebarState based on initial isCollapsed value
  const [sidebarState, setSidebarState] = useState<SidebarState>(() =>
    isMobile ? 'collapsed' : 'expanded'
  )
  const [isLogoHovered, setIsLogoHovered] = useState(false)
  const [isCollapseHovered, setIsCollapseHovered] = useState(false)
  const [isMobileExpanded, setIsMobileExpanded] = useState(false) // Track mobile overlay state
  const [showFeedbackModal, setShowFeedbackModal] = useState(false)
  const [showDoctorCallModal, setShowDoctorCallModal] = useState(false)

  // Handle feedback submission
  const handleSubmitFeedback = async (feedback: string) => {
    try {
      const result = await chatAPI.submitFeedback(feedback)
      if (result.success) {
        alert('Feedback erfolgreich gesendet. Vielen Dank!')
      } else {
        throw new Error(result.message || 'Feedback konnte nicht gesendet werden')
      }
    } catch (error) {
      console.error('Failed to submit feedback:', error)
      throw error
    }
  }

  // Update collapsed state when screen size changes
  useEffect(() => {
    if (isMobile) {
      setIsMobileExpanded(false)
    } else {
      // Desktop keeps its own collapsed state
    }
  }, [isMobile])

  // Handle transition states
  useEffect(() => {
    if (isCollapsed && (sidebarState === 'expanded' || sidebarState === 'transition-opening')) {
      // Starting to close
      setSidebarState('transition-closing')
      const timer = setTimeout(() => {
        setSidebarState('collapsed')
      }, 200) // Match animation duration
      return () => clearTimeout(timer)
    } else if (!isCollapsed && (sidebarState === 'collapsed' || sidebarState === 'transition-closing')) {
      // Starting to open
      setSidebarState('transition-opening')
      const timer = setTimeout(() => {
        setSidebarState('expanded')
      }, 200) // Match animation duration
      return () => clearTimeout(timer)
    }
  }, [isCollapsed, sidebarState])

  // Handle mobile expansion separately
  const handleMobileToggle = () => {
    setIsMobileExpanded(!isMobileExpanded)
  }

  return (
    <>
      {/* Desktop sidebar - hidden on mobile */}
      {!isMobile && (
        <motion.aside
          initial={{ x: -280 }}
          animate={{
            x: 0,
            width: isCollapsed ? 60 : 280
          }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
          className="relative flex h-full flex-col bg-(--outer-bg) z-10"
          style={{ minHeight: "100vh" }}
        >
          <div className="shrink-0">
            <SidebarHeader
              isCollapsed={isCollapsed}
              setIsCollapsed={setIsCollapsed}
              isLogoHovered={isLogoHovered}
              setIsLogoHovered={setIsLogoHovered}
              isCollapseHovered={isCollapseHovered}
              setIsCollapseHovered={setIsCollapseHovered}
              onNewChat={onNewChat}
            />

            <NewChatButton
              isCollapsed={isCollapsed}
              sidebarState={sidebarState}
              onNewChat={onNewChat}
            />
          </div>

          <ChatList
            chats={chats}
            activeChat={activeChat}
            isCollapsed={isCollapsed}
            sidebarState={sidebarState}
            onSelectChat={onSelectChat}
            onDeleteChat={onDeleteChat}
          />

          <CommunicationSection
            sidebarState={sidebarState}
            onFeedbackClick={() => setShowFeedbackModal(true)}
            onDoctorCallClick={() => setShowDoctorCallModal(true)}
          />

          <AuthSection sidebarState={sidebarState} />
        </motion.aside>
      )}

      {/* Mobile floating menu */}
      {isMobile && !isMobileExpanded && (
        <MobileFloatingMenu
          onOpenSidebar={handleMobileToggle}
          onNewChat={onNewChat}
          onOpenAccount={() => setIsMobileExpanded(true)}
        />
      )}

      {/* Mobile overlay when expanded */}
      {isMobile && isMobileExpanded && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsMobileExpanded(false)}
            className="fixed inset-0 bg-black/20 z-40"
          />
          <motion.aside
            initial={{ x: -280 }}
            animate={{ x: 0 }} // Full left position
            exit={{ x: -280 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="fixed left-0 top-0 bottom-0 w-[280px] flex flex-col bg-(--outer-bg) z-50 border-r border-border"
            style={{
              paddingBottom: "env(safe-area-inset-bottom, 0px)"
            }}
          >
            <div className="shrink-0">
              <SidebarHeader
                isCollapsed={false}
                setIsCollapsed={() => setIsMobileExpanded(false)}
                isLogoHovered={isLogoHovered}
                setIsLogoHovered={setIsLogoHovered}
                isCollapseHovered={isCollapseHovered}
                setIsCollapseHovered={setIsCollapseHovered}
                onNewChat={() => { onNewChat(); setIsMobileExpanded(false); }}
              />

              <NewChatButton
                isCollapsed={false}
                sidebarState="expanded"
                onNewChat={() => { onNewChat(); setIsMobileExpanded(false); }}
              />
            </div>

            <ChatList
              chats={chats}
              activeChat={activeChat}
              isCollapsed={false}
              sidebarState="expanded"
              onSelectChat={(id) => { onSelectChat(id); setIsMobileExpanded(false); }}
              onDeleteChat={onDeleteChat}
            />

            <CommunicationSection
              sidebarState="expanded"
              onFeedbackClick={() => { setShowFeedbackModal(true); setIsMobileExpanded(false); }}
              onDoctorCallClick={() => { setShowDoctorCallModal(true); setIsMobileExpanded(false); }}
            />

            <AuthSection
              sidebarState="expanded"
              onAction={() => setIsMobileExpanded(false)}
            />
          </motion.aside>
        </>
      )}

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        onSubmit={handleSubmitFeedback}
      />

      {/* Doctor Call Modal */}
      <DoctorCallModal
        isOpen={showDoctorCallModal}
        onClose={() => setShowDoctorCallModal(false)}
      />
    </>
  )
}